var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import translateIcon from '../../../../../assets/images/language-picker/translate-icon.svg';
import { getResetWidgets, getRoomDetailsRichMapId, } from '../../../../../store/uislice';
import { closeRoomDetailsPopup } from '../room-details-popup';
import { getCurrentLanguage, switchLanguage, } from '../../../../../utils/language';
import { LANGUAGE_OPTIONS, getNativeLanguageLabel, getTriggerLabel, resolveLanguageOption, } from './language-options';
const PickerWrap = styled.div `
  position: fixed;
  right: calc(1.65vw + clamp(8.75rem, 9.9vw, 10.35rem) + 0.7rem);
  bottom: 1.92vw;
  z-index: 3;

  @media (max-width: 768px) {
    right: calc(1rem + clamp(8.75rem, 38vw, 10rem) + 0.55rem);
    bottom: 1.7rem;
  }

  /* In portrait the picker would overlap the bottom action bar, so anchor it to
     the bottom-left corner instead. */
  @media (orientation: portrait) {
    right: auto;
    left: 3.5vw;
    /* Match the base of the center action bar (FloorMapButton bottom: 41px). */
    bottom: 41px;
  }
`;
const TriggerColumn = styled.div `
  position: relative;
  z-index: 2;
  width: clamp(8.45rem, 9.7vw, 9.55rem);
  padding: 0.42rem 0.46rem 0.48rem;
  border-radius: 0.86rem;
  background: linear-gradient(
    180deg,
    rgba(39, 47, 58, 0.97) 0%,
    rgba(31, 39, 49, 0.97) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(119, 136, 158, 0.14),
    0 14px 24px rgba(0, 0, 0, 0.26);

  @media (max-width: 768px) {
    width: min(9.55rem, calc(100vw - 2.8rem));
    padding: 0.4rem 0.44rem 0.46rem;
  }
`;
const TriggerLabel = styled.button `
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0 0 0.2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #b8c5d2;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.52rem, 0.6vw, 0.64rem);
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
`;
const TriggerIcon = styled.img `
  width: 0.82rem;
  height: 0.82rem;
  display: block;
  opacity: 0.9;
`;
const TriggerButton = styled.button `
  appearance: none;
  width: 100%;
  border: 1px solid rgba(133, 148, 166, 0.45);
  border-radius: 0.62rem;
  background: rgba(72, 86, 104, 0.96);
  box-shadow: inset 0 1px 0 rgba(191, 208, 230, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.42rem;
  padding: 0.46rem 0.52rem;
  color: #f6f8fb;
  cursor: pointer;
`;
const TriggerValue = styled.div `
  min-width: 0;
  text-align: left;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.68rem, 0.78vw, 0.8rem);
  font-weight: 500;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const TriggerChevron = styled.span `
  width: 0.36rem;
  height: 0.36rem;
  border-right: 0.09rem solid rgba(246, 248, 251, 0.92);
  border-bottom: 0.09rem solid rgba(246, 248, 251, 0.92);
  transform: ${({ $open }) => $open
    ? 'translateY(-0.04rem) rotate(-135deg)'
    : 'translateY(-0.12rem) rotate(45deg)'};
  flex: 0 0 auto;
`;
const PanelWrap = styled.div `
  position: absolute;
  right: -0.95rem;
  bottom: calc(100% + 1rem);
  width: clamp(17rem, 20vw, 21rem);
  z-index: 1;

  @media (max-width: 768px) {
    right: -0.5rem;
    width: min(21rem, calc(100vw - 2.8rem));
  }

  /* Left-anchored picker (portrait) opens the panel toward the right so it
     stays on screen. */
  @media (orientation: portrait) {
    right: auto;
    left: -0.95rem;
  }
`;
const PanelArrow = styled.div `
  position: absolute;
  left: 50%;
  bottom: -0.56rem;
  width: 1.12rem;
  height: 1.12rem;
  background: linear-gradient(
    180deg,
    rgba(39, 47, 58, 0.98) 0%,
    rgba(33, 41, 50, 0.98) 100%
  );
  transform: translateX(-50%) rotate(45deg);
  border-bottom-right-radius: 0.16rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.22);
`;
const Panel = styled.div `
  position: relative;
  overflow: hidden;
  border-radius: 1rem;
  background: linear-gradient(
    180deg,
    rgba(39, 47, 58, 0.98) 0%,
    rgba(33, 41, 50, 0.98) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(119, 136, 158, 0.14),
    0 24px 40px rgba(0, 0, 0, 0.3);
  padding: 0.55rem;
`;
const LanguageList = styled.div `
  position: relative;
  max-height: min(24rem, 46vh);
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  scroll-behavior: auto;
  padding-right: 0.12rem;
`;
const LanguageItem = styled.button `
  appearance: none;
  width: 100%;
  border: 0;
  border-radius: 0.62rem;
  background: ${({ $selected }) => ($selected ? '#3A4F66' : 'transparent')};
  border: ${({ $selected }) => $selected
    ? '1px solid rgba(111, 155, 213, 0.78)'
    : '1px solid transparent'};
  color: #f6f8fb;
  min-height: 3.25rem;
  padding: 0.56rem 0.62rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  cursor: pointer;
  text-align: left;

  & + & {
    margin-top: 0.14rem;
  }

  &:focus-visible {
    outline: 2px solid #b7d3f1;
    outline-offset: -2px;
  }
`;
const LanguageText = styled.div `
  min-width: 0;
`;
const LanguageName = styled.div `
  color: #f6f8fb;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.82rem, 0.9vw, 0.9rem);
  font-weight: 500;
  line-height: 1.15;
`;
const NativeLanguageName = styled.div `
  margin-top: 0.18rem;
  color: #cbd5e1;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.72rem, 0.78vw, 0.78rem);
  line-height: 1.25;
`;
const SelectedCheck = styled.span `
  width: 0.36rem;
  height: 0.66rem;
  border-right: 0.1rem solid rgba(246, 248, 251, 0.94);
  border-bottom: 0.1rem solid rgba(246, 248, 251, 0.94);
  transform: rotate(45deg);
  flex: 0 0 auto;
  margin-right: 0.14rem;
`;
const FooterRow = styled.div `
  display: flex;
  gap: 0.45rem;
  margin-top: 0.55rem;
`;
const ActionButton = styled.button `
  appearance: none;
  flex: 1 1 0;
  min-height: 2.2rem;
  border-radius: 0.62rem;
  border: ${({ $primary }) => $primary ? '0' : '1px solid rgba(133, 148, 166, 0.45)'};
  background: ${({ $primary }) => $primary ? '#3A4F66' : 'rgba(43, 52, 63, 0.92)'};
  box-shadow: ${({ $primary }) => $primary ? 'inset 0 1px 0 rgba(191, 208, 230, 0.1)' : 'none'};
  color: #f6f8fb;
  font-family: 'Sora', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
`;
// Capitalize the first letter of a (already localized) label. Some locale
// strings ship lowercase (e.g. "cancel"/"change"), so we uppercase in JS.
const capitalizeFirstLetter = (value) => {
    const [firstCharacter, ...remainingCharacters] = Array.from(value);
    if (!firstCharacter) {
        return value;
    }
    return `${firstCharacter.toLocaleUpperCase()}${remainingCharacters.join('')}`;
};
const LanguagePickerCard = () => {
    const { t, i18n } = useTranslation();
    const [selectedCode, setSelectedCode] = useState(() => resolveLanguageOption(i18n.resolvedLanguage || i18n.language || getCurrentLanguage()).code);
    const [draftCode, setDraftCode] = useState(selectedCode);
    const [focusedCode, setFocusedCode] = useState(selectedCode);
    const [isOpen, setIsOpen] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const listId = useId();
    const listRef = useRef(null);
    const selectedLanguageRef = useRef(null);
    const triggerRef = useRef(null);
    const restoreFocusRef = useRef(false);
    const typeAheadRef = useRef({ value: '', updatedAt: 0 });
    const resetWidgets = useSelector(getResetWidgets);
    const roomDetailsRichMapId = useSelector(getRoomDetailsRichMapId);
    const selectedOption = resolveLanguageOption(selectedCode);
    // Follow the language that actually loaded, including external changes and
    // resource fallback. The pending choice is only applied with Change.
    useEffect(() => {
        const appliedCode = resolveLanguageOption(i18n.resolvedLanguage || i18n.language || getCurrentLanguage()).code;
        setSelectedCode(appliedCode);
        setDraftCode(appliedCode);
        setFocusedCode(appliedCode);
    }, [i18n, i18n.language, i18n.resolvedLanguage]);
    // Position only when opening. Updating a pending choice must not move the
    // list. Setting this container's scrollTop also avoids scrolling the page.
    useLayoutEffect(() => {
        if (!isOpen)
            return;
        const list = listRef.current;
        const selected = selectedLanguageRef.current;
        if (!list || !selected)
            return;
        const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);
        const centered = selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2;
        list.scrollTop = Math.max(0, Math.min(centered, maxScroll));
        selected.focus({ preventScroll: true });
    }, [isOpen]);
    const closeAndRestoreFocus = () => {
        restoreFocusRef.current = true;
        setIsOpen(false);
    };
    useLayoutEffect(() => {
        var _a;
        if (!isOpen && !isApplying && restoreFocusRef.current) {
            restoreFocusRef.current = false;
            (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        }
    }, [isOpen, isApplying]);
    const applyLanguageChange = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsApplying(true);
        try {
            yield switchLanguage(draftCode);
            const appliedCode = i18n.resolvedLanguage || i18n.language || draftCode;
            document.documentElement.lang = appliedCode;
            document.documentElement.dir = 'ltr';
            setSelectedCode(resolveLanguageOption(appliedCode).code);
            closeAndRestoreFocus();
        }
        finally {
            setIsApplying(false);
        }
    });
    const handleCancel = () => {
        setDraftCode(selectedCode);
        closeAndRestoreFocus();
    };
    const handleToggleOpen = () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        setDraftCode(selectedCode);
        setFocusedCode(selectedCode);
        typeAheadRef.current = { value: '', updatedAt: 0 };
        closeRoomDetailsPopup();
        setIsOpen(true);
    };
    const focusOption = (code) => {
        const list = listRef.current;
        const option = list === null || list === void 0 ? void 0 : list.querySelector(`[data-language-code="${code}"]`);
        if (!list || !option)
            return;
        setFocusedCode(code);
        option.focus({ preventScroll: true });
        const top = option.offsetTop;
        const bottom = top + option.offsetHeight;
        // Keyboard navigation reveals only the part outside the list viewport.
        if (top < list.scrollTop) {
            list.scrollTop = top;
        }
        else if (bottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = bottom - list.clientHeight;
        }
    };
    const handleListKeyDown = (event) => {
        if (isApplying)
            return;
        const index = LANGUAGE_OPTIONS.findIndex((option) => option.code === focusedCode);
        let nextIndex;
        if (event.key === 'ArrowDown') {
            nextIndex = Math.min(index + 1, LANGUAGE_OPTIONS.length - 1);
        }
        else if (event.key === 'ArrowUp') {
            nextIndex = Math.max(index - 1, 0);
        }
        else if (event.key === 'Home') {
            nextIndex = 0;
        }
        else if (event.key === 'End') {
            nextIndex = LANGUAGE_OPTIONS.length - 1;
        }
        if (nextIndex !== undefined) {
            event.preventDefault();
            typeAheadRef.current = { value: '', updatedAt: 0 };
            focusOption(LANGUAGE_OPTIONS[nextIndex].code);
            return;
        }
        if (event.key.length === 1 &&
            event.key.trim() &&
            !event.altKey &&
            !event.ctrlKey &&
            !event.metaKey) {
            event.preventDefault();
            const now = Date.now();
            const previous = typeAheadRef.current;
            const query = (now - previous.updatedAt < 700 ? previous.value : '') +
                event.key.toLowerCase();
            typeAheadRef.current = { value: query, updatedAt: now };
            const match = LANGUAGE_OPTIONS.find((option) => option.englishLabel.toLowerCase().startsWith(query) ||
                getNativeLanguageLabel(option).toLowerCase().startsWith(query));
            if (match)
                focusOption(match.code);
        }
    };
    useEffect(() => {
        if (resetWidgets > 0)
            setIsOpen(false);
    }, [resetWidgets]);
    useEffect(() => {
        if (roomDetailsRichMapId)
            setIsOpen(false);
    }, [roomDetailsRichMapId]);
    return (_jsxs(PickerWrap, Object.assign({ "aria-label": "Language picker" }, { children: [isOpen && (_jsxs(PanelWrap, { children: [_jsx(PanelArrow, {}), _jsxs(Panel, Object.assign({ onKeyDown: (event) => {
                            if (event.key === 'Escape' && !isApplying) {
                                event.preventDefault();
                                handleCancel();
                            }
                        } }, { children: [_jsx(LanguageList, Object.assign({ id: listId, ref: listRef, role: "listbox", "aria-label": t('floor.choose-language'), "aria-busy": isApplying, onKeyDown: handleListKeyDown }, { children: LANGUAGE_OPTIONS.map((option) => {
                                    const isSelected = option.code === draftCode;
                                    const nativeLabel = getNativeLanguageLabel(option);
                                    return (_jsxs(LanguageItem, Object.assign({ type: "button", role: "option", "aria-selected": isSelected, "data-language-code": option.code, tabIndex: option.code === focusedCode ? 0 : -1, ref: isSelected ? selectedLanguageRef : null, "$selected": isSelected, disabled: isApplying, onFocus: () => setFocusedCode(option.code), onClick: () => {
                                            setDraftCode(option.code);
                                            setFocusedCode(option.code);
                                        } }, { children: [_jsxs(LanguageText, { children: [_jsx(LanguageName, Object.assign({ lang: "en" }, { children: option.englishLabel })), nativeLabel !== option.englishLabel && (_jsx(NativeLanguageName, { children: _jsx("bdi", Object.assign({ lang: option.code, dir: "auto" }, { children: nativeLabel })) }))] }), isSelected && _jsx(SelectedCheck, { "aria-hidden": "true" })] }), option.code));
                                }) })), _jsxs(FooterRow, { children: [_jsx(ActionButton, Object.assign({ type: "button", disabled: isApplying, onClick: handleCancel }, { children: capitalizeFirstLetter(t('floor.cancel')) })), _jsx(ActionButton, Object.assign({ type: "button", "$primary": true, disabled: isApplying, onClick: applyLanguageChange }, { children: capitalizeFirstLetter(t('floor.change')) }))] })] }))] })), _jsxs(TriggerColumn, { children: [_jsxs(TriggerLabel, Object.assign({ type: "button", "aria-haspopup": "listbox", "aria-expanded": isOpen, "aria-controls": isOpen ? listId : undefined, disabled: isApplying, onClick: handleToggleOpen }, { children: [_jsx(TriggerIcon, { src: translateIcon, alt: "", "aria-hidden": "true" }), _jsx("span", { children: t('floor.choose-language') })] })), _jsxs(TriggerButton, Object.assign({ ref: triggerRef, type: "button", "aria-haspopup": "listbox", "aria-expanded": isOpen, "aria-controls": isOpen ? listId : undefined, disabled: isApplying, onClick: handleToggleOpen }, { children: [_jsx(TriggerValue, { children: _jsx("bdi", Object.assign({ lang: selectedOption.code, dir: "auto" }, { children: getTriggerLabel(selectedOption) })) }), _jsx(TriggerChevron, { "$open": isOpen })] }))] })] })));
};
export default LanguagePickerCard;
